import * as ko from 'knockout';

import {LABELS} from "Core/Components/Translation/Locales";
import {MobileChecker} from "Core/Common/MobileChecker";

import NotificationTemplate from 'Core/Common/Templates/Notification.html';

interface IMessageType {
    Color: string;
    Title: string;
}

export class MessageTypes {
    get Warning(): IMessageType {
        return {Color: 'yellow', Title: this.GetTitle(LABELS.NOTIFIER_WARNING, 'Warning')};
    }

    get Success(): IMessageType {
        return {Color: 'green', Title: this.GetTitle(LABELS.NOTIFIER_SUCCESS, 'Success')};
    }

    get Fail(): IMessageType {
        return {Color: 'red', Title: this.GetTitle(LABELS.NOTIFIER_FAIL, 'Fail')};
    }

    private GetTitle(title: string, fallback: string): string {
        return isNaN(+title) ? title : fallback;
    }
}

interface INotification {
    Message: string;
    Type: IMessageType;
    ZIndex?: number
}

const DEFAULT_DURATION = 6000;
const BODY_TOP_OFFSET = 50;
const CONTAINER_TOP_OFFSET = 8;
const GAP = 8;
const MARGIN = 16;
const DISMISS_ANIMATION_MS = 280;
// Above the toast stack and any jBox modal the notification may be raised over.
const PERSISTENT_Z_INDEX = 1000010;

export const COLOR_TO_VARIANT = {
    green: 'success',
    red: 'destructive',
    yellow: 'warning'
};

export const VARIANT_ICONS = {
    success: 'fa-solid fa-circle-check',
    destructive: 'fa-solid fa-circle-xmark',
    warning: 'fa-solid fa-triangle-exclamation',
    info: 'fa-solid fa-circle-info'
};

const hostStacks = new Map<HTMLElement, Array<HTMLElement>>();

// Each persistent notification is its own element with its own Ok and closes on
// its own — they are never merged into one. What they share is a single backdrop
// and a centring pass, so several open at once neither dim twice nor overlap.
const persistentItems: Array<HTMLElement> = [];
let persistentOverlay: HTMLElement = null;

interface IAlertOptions {
    Variant: string;
    Title: string;
    Message: string;
    Compact?: boolean;
    // Toasts carry an X, persistent notifications an Ok on the trailing edge.
    ShowClose?: boolean;
    ShowOk?: boolean;
}

// Markup lives in Templates/Notification.html and is rendered through the
// renderTemplateXHtml extension, so toast and persistent notification share one
// definition of an alert. `html:` on the message keeps the markup support
// callers already rely on (<br>, links) that the old jBox Notice had.
function BuildAlert(options: IAlertOptions): HTMLElement {
    const html = (ko as any).renderTemplateXHtml(NotificationTemplate, {
        Variant: options.Variant,
        AlertClass: `sps-alert sps-alert-${options.Variant}${options.Compact ? ' sps-alert-sm' : ''}`,
        Icon: VARIANT_ICONS[options.Variant],
        Title: options.Title || '',
        Message: options.Message == null ? '' : options.Message,
        OkLabel: isNaN(+LABELS.OK) ? LABELS.OK : 'Ok',
        ShowOk: options.ShowOk === true,
        ShowClose: options.ShowClose === true
    });

    const holder = document.createElement('div');
    holder.innerHTML = html;

    return holder.firstElementChild as HTMLElement;
}

function VariantOf(type: IMessageType): string {
    return COLOR_TO_VARIANT[type && type.Color] || 'info';
}

// Same class jBox puts on its own overlay, so the dimming matches every other
// modal in the app. One backdrop regardless of how many notifications are open.
function EnsurePersistentOverlay() {
    if (persistentOverlay) {
        return;
    }

    persistentOverlay = document.createElement('div');
    persistentOverlay.className = 'sps-modal-backdrop sps-notification-overlay';
    persistentOverlay.setAttribute('data-testid', 'notification.overlay');
    persistentOverlay.style.zIndex = `${PERSISTENT_Z_INDEX - 1}`;
    document.body.appendChild(persistentOverlay);
}

// Centres the open notifications as a group. Each is positioned individually —
// there is no wrapper element holding them together.
function ReflowPersistent() {
    if (!persistentItems.length) {
        return;
    }

    const total = persistentItems.reduce((height, item) => height + item.offsetHeight, 0)
        + GAP * (persistentItems.length - 1);

    let top = Math.max(MARGIN, (window.innerHeight - total) / 2);
    for (const item of persistentItems) {
        item.style.top = `${top}px`;
        top += item.offsetHeight + GAP;
    }
}

function DismissPersistent(item: HTMLElement) {
    const index = persistentItems.indexOf(item);
    if (index === -1) {
        return;
    }

    persistentItems.splice(index, 1);
    item.remove();

    if (!persistentItems.length && persistentOverlay) {
        persistentOverlay.remove();
        persistentOverlay = null;
    }

    ReflowPersistent();
}

function ClosePersistent() {
    while (persistentItems.length) {
        DismissPersistent(persistentItems[0]);
    }
}


function ResolveHost(container: any): HTMLElement {
    if (container instanceof HTMLElement) {
        return container;
    }
    if (container && container.jquery && container.length && container[0] instanceof HTMLElement) {
        return container[0];
    }
    return document.body;
}

function Reflow(host: HTMLElement) {
    const stack = hostStacks.get(host) || [];
    let top = host === document.body ? BODY_TOP_OFFSET : CONTAINER_TOP_OFFSET;
    for (const toast of stack) {
        toast.style.top = `${top}px`;
        top += toast.offsetHeight + GAP;
    }
}

function Dismiss(host: HTMLElement, toast: HTMLElement) {
    const stack = hostStacks.get(host);
    if (!stack) {
        return;
    }
    const index = stack.indexOf(toast);
    if (index === -1) {
        return;
    }
    stack.splice(index, 1);
    toast.style.animation = `sps-toast-out ${DISMISS_ANIMATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`;
    setTimeout(() => {
        toast.remove();
        Reflow(host);
    }, DISMISS_ANIMATION_MS);
    Reflow(host);
}

export class Notifier {
    private _container: any;
    private _messageTypes: MessageTypes;

    constructor(container?: any) {
        this._container = container;
        this._messageTypes = new MessageTypes();
    }

    Show(notification: INotification, { duration, closeOnClick }: { duration?: number | null, closeOnClick?: boolean } = {}) {
        const host = ResolveHost(this._container);
        const variant = VariantOf(notification.Type);
        const title = (notification.Type && notification.Type.Title) || '';
        const autoCloseMs = duration === undefined ? DEFAULT_DURATION : (duration || 0);

        const toast = document.createElement('div');
        toast.className = 'sps-toast sps-toast-top-end';
        toast.setAttribute('role', 'alert');
        toast.style.insetInlineEnd = `${MARGIN}px`;
        toast.style.borderRadius = 'var(--radius)';
        toast.style.zIndex = `${notification.ZIndex || 1000000}`;
        if (host !== document.body) {
            toast.style.position = 'absolute';
        }

        const alert = BuildAlert({
            Variant: variant,
            Title: title,
            Message: notification.Message,
            Compact: MobileChecker.IsMobile(),
            ShowClose: true
        });

        toast.appendChild(alert);

        toast.querySelector('.sps-alert-close').addEventListener('click', (event) => {
            event.stopPropagation();
            Dismiss(host, toast);
        });
        if (closeOnClick !== false) {
            toast.addEventListener('click', () => Dismiss(host, toast));
        }

        host.appendChild(toast);
        const stack = hostStacks.get(host) || [];
        stack.unshift(toast);
        hostStacks.set(host, stack);
        Reflow(host);

        if (autoCloseMs > 0) {
            setTimeout(() => Dismiss(host, toast), autoCloseMs);
        }

        return {
            destroy: () => Dismiss(host, toast),
            close: () => Dismiss(host, toast)
        };
    }

    Success(message, { duration, closeOnClick}: { duration?: number | null, closeOnClick?: boolean } = {}) {
        this.Show({Message: message, Type: this._messageTypes.Success}, { duration, closeOnClick});

    }

    Failed(message, { duration, closeOnClick}: { duration?: number | null, closeOnClick?: boolean } = {}) {
        this.Show({Message: message, Type: this._messageTypes.Fail}, { duration, closeOnClick});

    }

    Warning(message, { duration, closeOnClick}: { duration?: number | null, closeOnClick?: boolean } = {}) {
        this.Show({Message: message, Type: this._messageTypes.Warning}, { duration, closeOnClick});
    }

    static Failed(message, { duration, closeOnClick}: { duration?: number | null, closeOnClick?: boolean } = {}) {
        new Notifier().Failed(message, { duration, closeOnClick});
    }

    static Warning(message, { duration, closeOnClick}: { duration?: number | null, closeOnClick?: boolean } = {}) {
        new Notifier().Warning(message, { duration, closeOnClick});
    }

    static Success(message, { duration, closeOnClick}: { duration?: number | null, closeOnClick?: boolean } = {}) {
        new Notifier().Success(message, { duration, closeOnClick});
    }

    // Centred, never auto-closes, dismissed only by the Ok button on its trailing
    // edge. Always hosted on document.body — a centred alert inside a scrolled
    // container would land off-screen. Repeated calls line up in one stack rather
    // than overlapping.
    ShowPersistent(notification: INotification) {
        EnsurePersistentOverlay();

        const item = document.createElement('div');
        item.className = 'sps-toast sps-toast-center';
        item.setAttribute('role', 'alert');
        item.setAttribute('data-testid', 'notification.persistent');
        item.style.zIndex = `${PERSISTENT_Z_INDEX}`;

        const alert = BuildAlert({
            Variant: VariantOf(notification.Type),
            Title: (notification.Type && notification.Type.Title) || '',
            Message: notification.Message,
            ShowOk: true
        });
        alert.querySelector('.sps-notification-ok').addEventListener('click', (event) => {
            event.stopPropagation();
            DismissPersistent(item);
        });

        item.appendChild(alert);
        document.body.appendChild(item);
        persistentItems.push(item);
        ReflowPersistent();

        return {
            close: () => DismissPersistent(item),
            destroy: () => DismissPersistent(item)
        };
    }

    PersistentSuccess(message) {
        return this.ShowPersistent({ Message: message, Type: this._messageTypes.Success });
    }

    PersistentFailed(message) {
        return this.ShowPersistent({ Message: message, Type: this._messageTypes.Fail });
    }

    PersistentWarning(message) {
        return this.ShowPersistent({ Message: message, Type: this._messageTypes.Warning });
    }

    static PersistentSuccess(message) {
        return new Notifier().PersistentSuccess(message);
    }

    static PersistentFailed(message) {
        return new Notifier().PersistentFailed(message);
    }

    static PersistentWarning(message) {
        return new Notifier().PersistentWarning(message);
    }

    static ClosePersistent() {
        ClosePersistent();
    }
}

export var messages = {
    GENERAL: {
        RECORD_CREATED: {
            title: "Success",
            message: "Record created",
            type: "success"
        },

        RECORD_UPDATED: {
            title: "Sucesss",
            message: "Record updated",
            type: "success"
        },

        RECORD_SAVED: {
            title: "Sucesss",
            message: "Record saved",
            type: "success"
        }
    },

    FORM_RESETED: {
        title: "Failed",
        message: "Form reseted.",
        type: "error"
    },

    QUERY_BUILDER: {
        SELECTED_FIELD: {
            title: "Failed",
            message: "Please select fields.",
            type: "error"
        },
        QUERY_EXECUTED: {
            title: "Success",
            message: "Query executed",
            type: "success"
        }
    }
};
